<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import CompanySortMenu from './components/CompanySortMenu.vue';
import CompanyMoreActions from './components/CompanyMoreActions.vue';

defineProps({
  showSearch: { type: Boolean, default: true },
  searchValue: { type: String, default: '' },
  headerTitle: { type: String, required: true },
  activeSort: { type: String, default: 'last_activity_at' },
  activeOrdering: { type: String, default: '' },
  hasActiveFilters: { type: Boolean, default: false },
});

const emit = defineEmits([
  'search',
  'update:sort',
  'create',
  'import',
  'export',
  'manage-labels',
  'manage-fields',
  'start-selection',
  'filter',
]);
</script>

<template>
  <header class="sticky top-0 z-20 px-6">
    <div
      class="flex items-start sm:items-center justify-between w-full py-6 gap-2 mx-auto max-w-5xl"
    >
      <span class="text-xl font-medium truncate text-n-slate-12">
        {{ headerTitle }}
      </span>
      <div class="flex items-center flex-col sm:flex-row flex-shrink-0 gap-4">
        <div v-if="showSearch" class="flex items-center gap-2 w-full">
          <Input
            :model-value="searchValue"
            type="search"
            :placeholder="$t('COMPANIES.SEARCH_PLACEHOLDER')"
            :custom-input-class="[
              'h-8 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8',
            ]"
            class="w-full"
            @input="emit('search', $event.target.value)"
          >
            <template #prefix>
              <Icon
                icon="i-lucide-search"
                class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
              />
            </template>
          </Input>
        </div>
        <div class="flex items-center flex-shrink-0 gap-2">
          <button
            class="relative flex items-center justify-center size-8 rounded-lg border transition-colors"
            :class="
              hasActiveFilters
                ? 'border-n-brand text-n-brand bg-n-alpha-1'
                : 'border-n-weak text-n-slate-11 hover:text-n-brand hover:border-n-brand'
            "
            :title="$t('COMPANIES.FILTER.TITLE')"
            @click="emit('filter')"
          >
            <Icon icon="i-lucide-list-filter" class="size-4" />
            <span
              v-if="hasActiveFilters"
              class="absolute -top-1 -right-1 size-2 rounded-full bg-n-brand"
            />
          </button>
          <CompanySortMenu
            :active-sort="activeSort"
            :active-ordering="activeOrdering"
            @update:sort="emit('update:sort', $event)"
          />
          <CompanyMoreActions
            @create="emit('create')"
            @import="emit('import')"
            @export="emit('export')"
            @manage-labels="emit('manage-labels')"
            @manage-fields="emit('manage-fields')"
            @start-selection="emit('start-selection')"
          />
          <Button
            :label="$t('COMPANIES.ACTIONS.CREATE')"
            icon="i-lucide-plus"
            size="sm"
            @click="emit('create')"
          />
        </div>
      </div>
    </div>
  </header>
</template>
